import { AxiosInstance } from 'axios';
import qs from 'qs';

// Relative, not the package-internal `@app/*` alias: consumers compile this module from source
// and resolve `@app/*` against their own baseUrl, where it points somewhere else entirely
import { CollectionResource } from '../../data/resources';

import {
    CreateTaskSubTicketInput,
    ReorderTaskSubTicketsInput,
    TaskSubTicketDesignerPickerFilterInput,
    TaskSubTicketStatusLogFilterInput,
    TaskSubTicketSummaryFilterInput,
    TaskSubTicketsFilterInput,
    UpdateTaskSubTicketAssigneeInput,
    UpdateTaskSubTicketInput,
    UpdateTaskSubTicketStatusInput,
} from './inputs';
import { TaskSubTicketRef } from './refs';
import {
    DesignerPickerItemResource,
    TaskSubTicketDetailResource,
    TaskSubTicketOrderResource,
    TaskSubTicketResource,
    TaskSubTicketStatusLogResource,
    TaskSubTicketSummaryResource,
} from './resources';

export default class TaskSubTicket {
    // No trailing slash: every single-resource and named-collection route appends its own `/…`
    // segment. The bare collection routes are the exception - the backend registers them as
    // `/` under the `/task-sub-ticket` prefix, so they append the slash explicitly below.
    // Putting the slash on `basePath` instead would double it on every other route.
    private basePath = '/api/task-sub-ticket';
    private http: AxiosInstance;

    constructor(http: AxiosInstance) {
        this.http = http;
    }

    // Get sub-tickets of a parent request
    get = async (filter: TaskSubTicketsFilterInput): Promise<CollectionResource<TaskSubTicketResource, {}>> => {
        const query = qs.stringify({ ...filter });

        // Canonical collection route is `/api/task-sub-ticket/`. Without the slash the backend
        // answers 301 and axios follows it, so this read only ever looked correct by accident
        const { data } = await this.http.get(`${this.basePath}/?${query}`);

        return data;
    };

    // Get sub-ticket counters of a parent request, for the aside summary block
    getSummary = async (filter: TaskSubTicketSummaryFilterInput): Promise<TaskSubTicketSummaryResource> => {
        const query = qs.stringify({ ...filter });

        const { data } = await this.http.get(`${this.basePath}/summary?${query}`);

        return data;
    };

    // Get sub-ticket status transitions of a parent request, for the request's own state log.
    // Named collection route, so no trailing slash - exactly like `/summary`
    getStatusLogs = async (
        filter: TaskSubTicketStatusLogFilterInput
    ): Promise<CollectionResource<TaskSubTicketStatusLogResource, {}>> => {
        const query = qs.stringify({ ...filter });

        const { data } = await this.http.get(`${this.basePath}/log?${query}`);

        return data;
    };

    // Set the display order of the parent request's non-archived sub-tickets. One transactional
    // write over the whole set, and the committed order comes back - what the client renders next
    // has to be what the server actually wrote
    reorder = async (input: ReorderTaskSubTicketsInput): Promise<TaskSubTicketOrderResource> => {
        const { data } = await this.http.put(`${this.basePath}/order`, input);

        return data;
    };

    // Get internal designers assignable to a sub-ticket of the parent request
    getDesignerPicker = async (
        filter: TaskSubTicketDesignerPickerFilterInput
    ): Promise<CollectionResource<DesignerPickerItemResource, {}>> => {
        const query = qs.stringify({ ...filter });

        const { data } = await this.http.get(`${this.basePath}/designer-picker?${query}`);

        return data;
    };

    // Get single sub-ticket with its change log and timer sessions
    getById = async (id: ID): Promise<TaskSubTicketDetailResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}`);

        return data;
    };

    // Create new sub-ticket
    create = async (input: CreateTaskSubTicketInput): Promise<TaskSubTicketRef> => {
        // Same canonical collection route, and here the slash is load-bearing: a POST to the
        // unslashed path is a 404, not a redirect, so creation silently never committed
        const { data } = await this.http.post(`${this.basePath}/`, input);

        return data;
    };

    // Update sub-ticket scope, expected deliverables and due date
    update = async (id: ID, input: UpdateTaskSubTicketInput): Promise<TaskSubTicketRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}`, input);

        return data;
    };

    // Change sub-ticket status
    updateStatus = async (id: ID, input: UpdateTaskSubTicketStatusInput): Promise<TaskSubTicketRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/status`, input);

        return data;
    };

    // Assign/reassign sub-ticket designer. Rejected while a timer is running on the sub-ticket
    updateAssignee = async (id: ID, input: UpdateTaskSubTicketAssigneeInput): Promise<TaskSubTicketRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/assignee`, input);

        return data;
    };

    // Archive sub-ticket
    archive = async (id: ID): Promise<TaskSubTicketRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}`);

        return data;
    };

    // Restore archived sub-ticket
    restore = async (id: ID): Promise<TaskSubTicketRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/restore`);

        return data;
    };
}
